// src/App.jsx
import React, { useMemo, useEffect } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import Header from "./components/Header.jsx";
import Footer from "./components/Footer.jsx";
import MobileBottomNav from "./components/MobileBottomNav.jsx";
import HomePage from "./pages/HomePage.jsx";
import FeaturedPage from "./pages/FeaturedPage.jsx";
import TrendingPage from "./pages/TrendingPage.jsx";
import WeekendPage from "./pages/WeekendPage.jsx";
import EventDetailsPage from "./pages/EventDetailsPage.jsx";
import CheckoutPage from "./pages/CheckoutPage.jsx";
import LoginPage from "./pages/LoginPage.jsx";
import RegisterPage from "./pages/RegisterPage.jsx";
import AdminRoute from "./admin/AdminRoute.jsx";
import AdminDashboard from "./admin/pages/AdminDashboard.jsx";
import AdminEventsList from "./admin/pages/events/AdminEventsList.jsx";
import AdminEventForm from "./admin/pages/events/AdminEventForm.jsx";
import AdminOrdersList from "./admin/pages/AdminOrdersList.jsx";
import AdminLayout from "./admin/AdminLayout.jsx";
import AdminEventView from "./admin/pages/events/AdminEventView.jsx";
import AdminEventEdit from "./admin/pages/events/AdminEventEdit.jsx";
import AdminBanner from "./admin/pages/banners/AdminBanner.jsx";
import AddEditBanner from "./admin/pages/banners/AdminBannerForm.jsx";
import PaymentSuccessful from "./pages/PaymentSuccessful.jsx";
import AdminCuppon from "./admin/pages/coupon/AdminCuppon.jsx";
import AdminCupponFrom from "./admin/pages/coupon/AdminCouponForm.jsx";
import TicketPage from "./pages/TicketPage.jsx";
import WishlistPage from "./pages/wishListPage.jsx";
import MyBookingsPage from "./pages/MyBookingsPage.jsx";
import ProfilePage from "./pages/profilePage.jsx";
import AdminReviewList from "./admin/pages/review/AdminReviewList.jsx";
import AboutPage from "./pages/AboutPage.jsx";
import ContactPage from "./pages/ContactPage.jsx";
import CareersPage from "./pages/CareersPage.jsx";
import ScrollToTop from "./components/ScrollToTop.jsx";
import AdminPaymentList from "./admin/pages/payment/AdminPaymentList.jsx";
import AdminBookingNotifications from "./admin/pages/notifications/AdminBookingNotifications.jsx";
import PaymentReconciliation from "./admin/pages/payment/PaymentReconciliation.jsx";
import PaymentErrors from "./admin/pages/payment/PaymentErrors.jsx";
import AdminCashBooking from "./admin/pages/cashBooking/AdminCashBooking.jsx";
import AdminBookingList from "./admin/pages/cashBooking/AdminBookingsList.jsx";
import AdminViewBooking from "./admin/pages/cashBooking/AdminViewBooking.jsx";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import TermsOfUsePage from "./pages/TermsPage.jsx";
import PrivacyPage from "./pages/PrivacyPage.jsx";
import AdminQRScanMobile from "./admin/pages/AdminQRScanMobile.jsx";
import ListYourShowPage from "./listYourShow/ListYourShowPage.jsx";
import ListYourShowForm from "./listYourShow/ListYourShowForm.jsx";
import ListYourShowEdit from "./listYourShow/ListYourShowEdit.jsx";
import ListYourShowView from "./listYourShow/ListYourShowView.jsx";
import AdminUserList from "./admin/pages/AdminUserList.jsx";
import Support from "./pages/Support.jsx";
import Settings from "./pages/Settings.jsx";
import AdminEeventDashboard from "./admin/pages/events/AdminEventDashboard.jsx";
import ListYourShowEventDashboard from "./listYourShow/ListYourShowEventDashboard.jsx";
import OrganizerRoute from "./organizer/OrganizerRoute.jsx";
import OrganizerLayout from "./organizer/OrganizerLayout.jsx";
import LayoutPage from "./pages/Layout.jsx";
import AllBookingList from "./admin/pages/order/AllBookingList.jsx";
import InstallPWA from "./components/InstallPWA.jsx";
import ShippingPolicyPage from "./pages/ShippingPolicy.jsx";
import CancellationRefundPolicyPage from "./pages/CancellationPolicy.jsx";
import  PrivacyPolicyPage from "./pages/PrivacyPolicyPage.jsx";
import FAQPage from "./pages/FAQPage.jsx";
/* ──────────────────────────────────────────────
   SEO helpers (React 19 Document Metadata)
   ────────────────────────────────────────────── */
function DefaultSEO() {
  const SITE_NAME = (import.meta.env.VITE_SITE_NAME || "ShowTimeBro").trim();
  const SITE_URL = (import.meta.env.VITE_SITE_URL || "https://showtimebro.com").replace(/\/+$/, "");
  const OG_IMAGE = import.meta.env.VITE_OG_IMAGE || `${SITE_URL}/og-image.jpg`;
  const DESC =
    "Book Donor Passes for live shows, concerts, sports and weekend events. Discover trending events near you on ShowTimeBro.";

  return (
    <>
      <title>{SITE_NAME}</title>
      <meta name="description" content={DESC} />
      <link rel="canonical" href={SITE_URL} />

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={SITE_URL} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:title" content={SITE_NAME} />
      <meta property="og:description" content={DESC} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={SITE_NAME} />
      <meta name="twitter:description" content={DESC} />
      <meta name="twitter:image" content={OG_IMAGE} />
    </>
  );
}

function SeoForRoute() {
  const { pathname } = useLocation();
  const SITE_NAME = (import.meta.env.VITE_SITE_NAME || "ShowTimeBro").trim();
  const BASE_URL = (import.meta.env.VITE_SITE_URL || "https://showtimebro.com").replace(/\/+$/, "");
  const url = `${BASE_URL}${pathname}`;

  const meta = useMemo(() => {
    let title = SITE_NAME;
    let desc =
      "Book Donor Passes for live shows, concerts, sports and weekend events. Discover trending and featured events near you.";
    let robots = "index,follow";

    if (pathname === "/") {
      title = "Book Events, Shows & Sports";
      desc = "Discover trending, featured and weekend events. Book Donor Passes instantly on ShowTimeBro.";
    } else if (pathname.startsWith("/featured")) {
      title = "Featured Events";
      desc = "Handpicked featured events and shows you shouldn’t miss.";
    } else if (pathname.startsWith("/trending")) {
      title = "Trending Now";
      desc = "See what's trending today—concerts, stand-up, theatre and more.";
    } else if (pathname.startsWith("/weekend")) {
      title = "This Weekend";
      desc = "Plan your weekend with top events and experiences around you.";
    } else if (pathname.startsWith("/events/")) {
      title = "Event Details";
      desc = "All details, timing, venue and Donor Passes for this event.";
    } else if (pathname.startsWith("/checkout/")) {
      title = "Checkout";
      desc = "Secure checkout for your Donor Passes.";
      robots = "noindex,nofollow";
    } else if (pathname === "/login") {
      title = "Login";
      desc = "Login to manage your bookings and wishlist.";
      robots = "noindex,nofollow";
    } else if (pathname === "/register") {
      title = "Create Account";
      desc = "Register to book faster and track your orders.";
      robots = "noindex,nofollow";
    } else if (pathname === "/tickets") {
      title = "My Donor Passes";
      desc = "Access and download your event Donor Passes.";
      robots = "noindex,nofollow";
    } else if (pathname === "/wishlist") {
      title = "Wishlist";
      desc = "Your saved events and shows.";
      robots = "noindex,nofollow";
    } else if (pathname === "/my-bookings") {
      title = "My Bookings";
      desc = "View your bookings history and invoices.";
      robots = "noindex,nofollow";
    } else if (pathname === "/profile") {
      title = "Profile";
      desc = "Manage your profile and preferences.";
      robots = "noindex,nofollow";
    } else if (pathname === "/about") {
      title = "About Us";
      desc = "Learn more about ShowTimeBro and our mission.";
    } else if (pathname === "/contact") {
      title = "Contact Us";
      desc = "Get in touch with our support team.";
    } else if (pathname === "/careers") {
      title = "Careers";
      desc = "Work with us—see open roles at ShowTimeBro.";
    } else if (pathname.startsWith("/payment-successful")) {
      title = "Payment Successful";
      desc = "Your payment is successful. Check your email for Donor Passes.";
      robots = "noindex,nofollow";
    } else if (pathname.startsWith("/admin")) {
      title = "Admin";
      desc = "Administration dashboard for events, orders and payments.";
      robots = "noindex,nofollow";
    }
    // else if (pathname === "/terms-and-conditions") {
    //   title = "Terms & Conditions";
    //   desc = "Terms and conditions for using ShowTimeBro.";
    // } else if (pathname === "/privacy-policy") {
    //   title = "Privacy Policy";
    //   desc = "How ShowTimeBro collects and uses your data.";
    // } 
    else {
      title = "Explore Events";
      desc = "Explore events, book Donor Passes and enjoy live experiences.";
    }

    return { title, desc, robots, url };
  }, [pathname]);

  return (
    <>
      <title>{meta.title} | {SITE_NAME}</title>
      <meta name="description" content={meta.desc} />
      <meta name="robots" content={meta.robots} />
      <link rel="canonical" href={meta.url} />
      <meta property="og:title" content={`${meta.title} | ${SITE_NAME}`} />
      <meta property="og:description" content={meta.desc} />
      <meta property="og:url" content={meta.url} />
      <meta name="twitter:title" content={`${meta.title} | ${SITE_NAME}`} />
      <meta name="twitter:description" content={meta.desc} />
    </>
  );
}


function AppInner() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  const isOrganizerConsole =
    location.pathname.startsWith("/listyourshow") ||
    /^\/events\/dashboard\//.test(location.pathname);
  const hideMarketingChrome = isAdmin || isOrganizerConsole;

  const hideMobileBottomNav =
    hideMarketingChrome ||
    /^\/(login|register)(\/|$)/.test(location.pathname) ||
    location.pathname.startsWith("/payment-successful");

  useEffect(() => {
    // optional: admin pages skip karna ho to guard rakho
    if (typeof window !== "undefined" && window.fbq && !hideMarketingChrome) {
      window.fbq("track", "PageView");
    }
  }, [location.pathname, hideMarketingChrome]);

  return (
    <div
      className={`min-h-full flex w-full min-w-0 max-w-full flex-col overflow-x-clip text-left antialiased ${
        hideMobileBottomNav ? "" : "pb-[4.75rem] md:pb-0"
      }`}
    >
      {/* Global defaults + per-route overrides */}
      <DefaultSEO />
      <SeoForRoute />

      {!hideMarketingChrome && <Header />}
      <ScrollToTop behavior="smooth" />

      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/featured" element={<FeaturedPage />} />
        <Route path="/trending" element={<TrendingPage />} />
        <Route path="/weekend" element={<WeekendPage />} />
        {/* Must be before `/events/:id` or "dashboard" is captured as the event id */}
        <Route path="/events/dashboard/:id" element={<OrganizerRoute />}>
          <Route element={<OrganizerLayout />}>
            <Route index element={<ListYourShowEventDashboard />} />
          </Route>
        </Route>
        <Route path="/events/:id" element={<EventDetailsPage />} />
        <Route path="/checkout/:id" element={<CheckoutPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/payment-successful" element={<PaymentSuccessful />} />
        <Route path="/tickets" element={<TicketPage />} />
        <Route path="/wishlist" element={<WishlistPage />} />
        <Route path="/my-bookings" element={<MyBookingsPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/terms-and-condition" element={<TermsOfUsePage />} />
        <Route path="/Privacy" element={<PrivacyPage />} />
        <Route path="/scanner" element={<AdminQRScanMobile />} />

        <Route path="/listyourshow" element={<OrganizerRoute />}>
          <Route element={<OrganizerLayout />}>
            <Route index element={<ListYourShowPage />} />
            <Route path="new" element={<ListYourShowForm />} />
            <Route path="edit/:id" element={<ListYourShowEdit />} />
            <Route path="view/:id" element={<ListYourShowView />} />
          </Route>
        </Route>

        <Route path="/support" element={<Support />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/LayoutPage" element={<LayoutPage />} />
        <Route path="/shipping" element={<ShippingPolicyPage />} />
        <Route path="/cancellation" element={<CancellationRefundPolicyPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/faq" element={<FAQPage />} />


        {/* <Route path="/careers" element={<CareersPage />} /> */}

        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="events" element={<AdminEventsList />} />
          <Route path="events/new" element={<AdminEventForm />} />
          <Route path="events/dashboard/:id" element={<AdminEeventDashboard />} />
          <Route path="events/edit/:id" element={<AdminEventEdit />} />
          <Route path="events/:id" element={<AdminEventView />} />
          <Route path="orders" element={<AdminOrdersList />} />
          <Route path="banners" element={<AdminBanner />} />
          <Route path="banners/new" element={<AddEditBanner />} />
          <Route path="banners/edit/:id" element={<AddEditBanner />} />
          <Route path="coupon" element={<AdminCuppon />} />
          <Route path="coupon/new" element={<AdminCupponFrom />} />
          <Route path="coupon/edit/:id" element={<AdminCupponFrom />} />
          <Route path="review" element={<AdminReviewList />} />
          <Route path="payment" element={<AdminPaymentList />} />
          <Route path="booking-notifications" element={<AdminBookingNotifications />} />
          <Route path="payment-reconciliation" element={<PaymentReconciliation />} />
          <Route path="payment-errors" element={<PaymentErrors />} />
          <Route path="cash-booking" element={<AdminCashBooking />} />
          <Route path="bookings" element={<AdminBookingList />} />
          <Route path="bookings/:id" element={<AdminViewBooking />} />
          <Route path="users" element={<AdminUserList />} />
          <Route path="allBookingList" element={<AllBookingList />} />




        </Route>

        {/* Unknown URLs → homepage (no 404 page) */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* {!isAdmin && <Footer />}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      /> */}

      {!hideMarketingChrome && (
        <div className="hidden md:block">
          <Footer />
        </div>
      )}

      {!hideMobileBottomNav && <MobileBottomNav />}

      {/* PWA install button – sirf non-admin / non-organizer-console pages ke liye */}
      {!hideMarketingChrome && <InstallPWA />}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />

    </div>
  );
}

export default function App() {
  return <AppInner />;
}

